import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import PredictInput from '../components/PredictInput'
import { useAuth } from '../lib/auth'

/**
 * Patrol missions — spaced repetition wearing a uniform.
 *
 * The most valuable fifteen minutes available in this app, which is why it pays
 * well and why it is one click from the dashboard. Questions you keep missing
 * come back tomorrow; ones you know drift out toward monthly.
 */

interface Question {
  kind: string
  id: string
  prompt: string
  lessonTitle: string
  moduleSlug: string
  lessonSlug: string
  choices: { id: string; text: string }[]
}

interface Result {
  correct: number
  total: number
  xpGranted: number
  leveledUp: boolean
  feedback: {
    questionId: string
    correct: boolean
    correctChoiceIds: string[]
    explanation: string
    nextDueDays: number
  }[]
  game: { level: number; totalXp: number; progress: number; streak: number; longestStreak: number }
}

export default function Patrol() {
  const { user, game, setGame } = useAuth()
  const [questions, setQuestions] = useState<Question[] | null>(null)
  const [selected, setSelected] = useState<Record<string, string[]>>({})
  // Predict answers are free text — same split as Quiz.tsx and Boss.tsx.
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [result, setResult] = useState<Result | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!user) return
    api<{ questions: Question[] }>('/game/patrol')
      .then((d) => setQuestions(d.questions))
      .catch(() => setQuestions([]))
  }, [user])

  async function submit() {
    setBusy(true)
    try {
      // Built from `questions`, not the answer stores: a question left out of
      // the payload is never graded, and an ungraded patrol question is also
      // never RESCHEDULED — so skipping it would quietly drop it out of the
      // spaced-repetition queue instead of marking it for another look.
      const answers = (questions ?? []).map((q) =>
        q.kind === 'predict'
          ? { questionId: q.id, text: typed[q.id] ?? '' }
          : { questionId: q.id, choiceIds: selected[q.id] ?? [] },
      )
      const res = await api<Result>('/game/patrol/submit', { method: 'POST', body: { answers } })
      setResult(res)
      if (game) setGame({ ...game, ...res.game })
    } finally {
      setBusy(false)
    }
  }

  if (!user) {
    return (
      <div className="patrol-page">
        <p className="notice">Sign in to run a patrol.</p>
        <Link to="/login" className="button button-primary">Sign in</Link>
      </div>
    )
  }

  if (questions === null) return <div className="loading">Assembling your patrol…</div>

  if (questions.length === 0 && !result) {
    return (
      <div className="patrol-page">
        <h1>🛡 Patrol</h1>
        <div className="notice">
          <p>
            <strong>Nothing due.</strong> Patrols draw from lessons you have completed — finish a
            few and questions will start cycling back on a schedule.
          </p>
          <Link to="/">Back to the tower →</Link>
        </div>
      </div>
    )
  }

  if (result) {
    return (
      <div className="patrol-page">
        <h1>🛡 Patrol complete</h1>
        <div className={result.correct === result.total ? 'result result-pass' : 'result result-fail'}>
          <strong>{result.correct} / {result.total}</strong>
          <span>+{result.xpGranted} XP</span>
          {result.leveledUp && <span className="levelup">LEVEL {result.game.level}!</span>}
        </div>

        <ol className="questions">
          {result.feedback.map((f, i) => (
            <li key={f.questionId} className="question">
              <p className={f.correct ? 'explain explain-ok' : 'explain explain-no'}>
                {f.correct ? '✅ ' : '❌ '}
                <strong>Q{i + 1}.</strong> {f.explanation}
              </p>
              <p className="dim schedule-note">
                {f.correct ? (
                  f.nextDueDays === 1 ? (
                    <>
                      Back tomorrow. First correct answers always repeat once — seeing something
                      once is not evidence you know it. Get it right again and the gap widens.
                    </>
                  ) : (
                    <>Back in {f.nextDueDays} days, and further out each time you get it right.</>
                  )
                ) : (
                  <>Back tomorrow, and it will keep returning more often until it sticks.</>
                )}
              </p>
            </li>
          ))}
        </ol>

        <div className="lesson-footer">
          <Link to="/dashboard" className="button">Dashboard</Link>
          <Link to="/" className="button">Tower</Link>
        </div>
      </div>
    )
  }

  const answeredAll = questions.every((q) =>
    q.kind === 'predict'
      ? (typed[q.id] ?? '').trim().length > 0
      : (selected[q.id]?.length ?? 0) > 0,
  )

  return (
    <div className="patrol-page">
      <h1>🛡 Patrol</h1>
      <p className="dim">
        {questions.length} question{questions.length === 1 ? '' : 's'} due. Miss one and it returns
        tomorrow; get it right and it moves further out.
      </p>

      <ol className="questions">
        {questions.map((q, i) => (
          <li key={q.id} className="question">
            <p className="question-prompt">
              <span className="dim">{i + 1}.</span> {q.prompt}
            </p>
            <p className="dim question-source">
              from <Link to={`/floor/${q.moduleSlug}/${q.lessonSlug}`}>{q.lessonTitle}</Link>
            </p>
            {q.kind === 'predict' ? (
              <PredictInput
                id={q.id}
                value={typed[q.id] ?? ''}
                disabled={Boolean(result)}
                onChange={(next) => setTyped((prev) => ({ ...prev, [q.id]: next }))}
              />
            ) : (
            <div className="choices" role="radiogroup" aria-label={q.prompt}>
              {q.choices.map((ch) => (
                <button
                  key={ch.id}
                  type="button"
                  role="radio"
                  aria-checked={selected[q.id]?.includes(ch.id) ?? false}
                  className={selected[q.id]?.includes(ch.id) ? 'choice choice-selected' : 'choice'}
                  onClick={() => setSelected((p) => ({ ...p, [q.id]: [ch.id] }))}
                >
                  {ch.text}
                </button>
              ))}
            </div>
            )}
          </li>
        ))}
      </ol>

      <button
        type="button"
        className="button button-primary button-full"
        onClick={() => void submit()}
        disabled={!answeredAll || busy}
      >
        {busy ? 'Filing report…' : answeredAll ? 'Complete patrol' : 'Answer every question first'}
      </button>
    </div>
  )
}
